// Runtime endpoints. Injected by vite.config.ts from the repo-root .env (API_URL, GATEWAY_URL, SITE_URL);
// the desktop shell overrides them through window.moodsDesktop.config (Phase 12 bridge, packages/shared desktop.ts),
// the iOS and Android apps through window.moodsNative.config (packages/shared native.ts).
import type { CapacitorBridgeGlobal, MoodsDesktopBridge, MoodsNativeShell } from '@moods/shared';

declare global {
  interface Window {
    moodsDesktop?: MoodsDesktopBridge;
    moodsNative?: MoodsNativeShell;
    Capacitor?: CapacitorBridgeGlobal;
  }
}

const desktop = typeof window !== 'undefined' ? window.moodsDesktop?.config : undefined;
const shell = typeof window !== 'undefined' ? window.moodsNative : undefined;
const native = shell?.platform === 'ios' || shell?.platform === 'android' ? shell : undefined;

export const API_URL: string = (desktop?.apiUrl ?? native?.config.apiUrl ?? import.meta.env.VITE_API_URL ?? 'http://localhost:4000').replace(/\/$/, '');
export const GATEWAY_URL: string = desktop?.gatewayUrl ?? native?.config.gatewayUrl ?? import.meta.env.VITE_GATEWAY_URL ?? 'ws://localhost:4000/gateway';
export const SITE_URL: string = (desktop?.siteUrl ?? native?.config.siteUrl ?? import.meta.env.VITE_SITE_URL ?? 'http://localhost:4321').replace(/\/$/, '');
export const IS_DESKTOP = typeof window !== 'undefined' && Boolean(window.moodsDesktop);
/** The MOODS iOS app (apps/ios): WKWebView — no screen share, no global shortcuts, push through APNs (lib/native.ts). */
export const IS_IOS_APP = native?.platform === 'ios';
/** The MOODS Android app (apps/android): Android's web view — no screen share, push through FCM (lib/native.ts). */
export const IS_ANDROID_APP = native?.platform === 'android';
/**
 * A MOODS phone app (iOS or Android, Capacitor): what a web view inside an app cannot do (screen share, web push,
 * desktop notifications) and what a store asks of it (the Market's digital goods) is gated on this; what only iOS
 * does differently stays on IS_IOS_APP.
 */
export const IS_NATIVE_APP = IS_IOS_APP || IS_ANDROID_APP;
/**
 * The app's native voice engine (iOS and Android 1.1): the call's audio is native (it mixes with other apps and goes
 * on behind the lock screen) and per-user volume works; the web view joins as its companion.
 */
export const NATIVE_VOICE = IS_NATIVE_APP && Boolean(native?.features?.includes('voice'));
/** iOS ignores a media element's volume: without the native engine the phone's buttons set it (no per-user volume) */
export const FIXED_VOICE_VOLUME = IS_IOS_APP && !NATIVE_VOICE;
/**
 * The web build bundled inside a MOODS app (desktop, iOS on `app://moods`, Android on `https://moods`): cookies never
 * reach the API from there (tokens travel in the body and the shell keeps them), no service worker, and a new build
 * arrives with the app.
 */
export const IS_APP_SHELL = IS_DESKTOP || IS_NATIVE_APP;
export const PLATFORM: 'web' | 'desktop-win' | 'desktop-mac' | 'mobile-web' | 'ios' | 'android' = IS_DESKTOP
  ? window.moodsDesktop?.platform === 'darwin'
    ? 'desktop-mac'
    : 'desktop-win'
  : IS_IOS_APP
    ? 'ios'
    : IS_ANDROID_APP
      ? 'android'
      : typeof navigator !== 'undefined' && /Mobi|Android|iPhone/.test(navigator.userAgent)
        ? 'mobile-web'
        : 'web';
